import { useCallback, useMemo } from 'react';
import {
    CardPaymentTableRecordData,
    InvoicePaymentType,
    PaymentCardFormat,
    PaymentTableRecord,
    PaymentType,
    PreauthorizationActions,
} from '@mews-commander/data-access';
import { Amount, WithAccountId } from '@mews-commander/types';
import { lt, useLocalizeText } from '@mews/framework';
import { TextKey, RouteName, DataTestId } from '@mews-commander/enums';
import { UnicodeEntity } from '@mews/enums';
import { CommanderFormProps, useDialogForm } from '@mews-commander/form';
import { useResolveRoute } from '@mews/routing';
import { sumAllAmounts, useCommanderSelector, useCommanderThunkDispatch } from '@mews-commander/utils';
import { getCurrentEnterpriseDefaultCurrencyCode } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { PaymentRefundDialog } from '../../forms/PaymentRefund/PaymentRefundDialog';
import { openModal } from '../../../../app';
import { showReservationDetail } from '../../../../routes';
import { ReservationTab } from '../../../../../types/reservationTab';
import { useGetPreauthorizationsTableRecords } from '../../../queries';

export const useGetTableRecordsTotalAmount = (records: PaymentTableRecord[]) => {
    const defaultEnterpriseCurrencyCode: string = useCommanderSelector(getCurrentEnterpriseDefaultCurrencyCode);
    return useMemo(() => {
        const amounts = records
            .filter(({ data }) => {
                return !(
                    data.discriminator === PaymentType.Invoice && data.value.type === InvoicePaymentType.Overpayment
                );
            })
            .map(({ amount }) => amount) as Amount[];
        return sumAllAmounts(amounts, defaultEnterpriseCurrencyCode);
    }, [records, defaultEnterpriseCurrencyCode]);
};

export const useLocalizePaymentCardDetails = () => {
    const localizeText = useLocalizeText();

    return ({ paymentCardType, lastFourCardDigits, paymentCardFormat }: CardPaymentTableRecordData) => {
        const paymentCardFormatSuffix =
            paymentCardFormat === PaymentCardFormat.Virtual
                ? `${UnicodeEntity.NoBreakSpace}${localizeText({
                      textKey: TextKey.CreditCardFormatVirtual,
                  })}`
                : '';

        return `${paymentCardType}${UnicodeEntity.NoBreakSpace}${lastFourCardDigits}${paymentCardFormatSuffix}`;
    };
};

export const useRefundPaymentDialog = ({
    paymentId,
    accountId,
    ...commanderFormProps
}: { paymentId: string } & WithAccountId & Partial<CommanderFormProps>) =>
    useDialogForm({
        title: lt('PaymentRefundFormHeader'),
        renderForm: (props) => <PaymentRefundDialog paymentId={paymentId} accountId={accountId} {...props} />,
        saveLabel: lt('Refund'),
        submitButtonDataTestId: DataTestId.RefundPaymentSubmitButton,
        ...commanderFormProps,
    });

export const useOpenCashierDetailModal = () => {
    const dispatch = useCommanderThunkDispatch();
    const resolveRoute = useResolveRoute();
    return useCallback(
        (id: string) => dispatch(openModal(resolveRoute(RouteName.CashierDetail, { id }))),
        [dispatch, resolveRoute]
    );
};

export const useOpenReservationDetailModal = () => {
    const dispatch = useCommanderThunkDispatch();
    return useCallback(
        (id: string) => dispatch(showReservationDetail({ reservationId: id, reservationTab: ReservationTab.Status })),
        [dispatch]
    );
};

export const useHasChargeablePreauthorization = ({ accountId }: WithAccountId) => {
    const { data } = useGetPreauthorizationsTableRecords({ accountId });

    return data?.some(({ actions }) => actions.includes(PreauthorizationActions.ChargePreauthorization));
};
